import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'norvig_lang_toast_seen';
const SHOW_DELAY_MS = 500;
const AUTO_DISMISS_MS = 12000;

// Routes that only exist in Norwegian — suggesting a language switch there is noise.
const NO_ONLY_PREFIXES = ['/investors', '/kommuneoversikt', '/admin', '/book-prat'];
// Kommuneflatene er norskspråklige — banneret er støy der.
const EMPLOYER_NO_ONLY_PREFIXES = ['/dashboard', '/profil', '/profiles', '/messages'];

/**
 * One-time toast that appears the FIRST time we auto-detect a non-Norwegian
 * browser and default to EN. Lets the user switch to NO in a single click.
 * Hardcoded English copy by design — the user is on an EN session.
 */
const LangAutoDetectToast = () => {
  const { autoDetectedToEn, language, changeLanguage } = useLanguage();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const onNorwegianOnlyPage =
    NO_ONLY_PREFIXES.some((p) => pathname.startsWith(p)) ||
    (user?.role === 'employer' && EMPLOYER_NO_ONLY_PREFIXES.some((p) => pathname.startsWith(p)));

  // Only consider opening once, on mount, when conditions are met.
  useEffect(() => {
    if (onNorwegianOnlyPage) return;
    if (!autoDetectedToEn) return;
    if (language !== 'en') return;
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(STORAGE_KEY) === '1') return;

    const showTimer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(showTimer);
  }, [autoDetectedToEn, language, onNorwegianOnlyPage]);

  // Auto-dismiss after 12 seconds without interaction (only while visible).
  useEffect(() => {
    if (!open || onNorwegianOnlyPage) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, '1');
      } catch {
        // ignore
      }
      setOpen(false);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [open]);

  const persist = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
  };

  // Lukk når brukeren selv bytter språk (f.eks. via header-toggelen).
  useEffect(() => {
    if (open && language !== 'en') {
      persist();
      setOpen(false);
    }
  }, [language, open]);

  const dismiss = () => {
    persist();
    setOpen(false);
  };

  const switchToNo = () => {
    changeLanguage('no');
    persist();
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && !onNorwegianOnlyPage && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          role="status"
          aria-live="polite"
          className="fixed z-[70] inset-x-4 bottom-6 mx-auto max-w-[480px] sm:left-6 sm:right-auto sm:bottom-6 sm:inset-x-auto sm:mx-0"
          data-testid="lang-auto-detect-toast"
        >
          <div
            className="flex items-start gap-3 rounded-2xl bg-bone border border-mist shadow-[0_12px_36px_rgba(10,14,27,0.10)] px-5 py-4"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink leading-relaxed">
                Showing English based on your browser.{' '}
                <button
                  onClick={switchToNo}
                  className="font-medium text-ink hover:text-ink underline underline-offset-4 decoration-ink/30 hover:decoration-ink transition-colors"
                  data-testid="lang-toast-switch-no"
                >
                  Switch to Norwegian
                </button>
              </p>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="flex-none rounded-full p-1 text-mute hover:text-ink hover:bg-ink/[0.05] transition-colors"
              data-testid="lang-toast-dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LangAutoDetectToast;
